/**
 * TasksPage.tsx — Task Manager como LISTA DE ACORDEONES.
 *
 * Decisión de producto: el detalle de tarea no existe como pantalla — todo
 * vive en el acordeón (TaskAccordion) dentro de la lista. La ruta /tasks/:id
 * se eliminó; los enlaces viejos llevan acá con ?open=<id> y esa tarea queda
 * expandida con scroll a su posición.
 *
 * Bandas por ESTADO (no por tiempo): Requieren aprobación · En curso ·
 * Cerradas — lo que espera una decisión tuya siempre arriba. Varias tareas
 * abiertas a la vez (mapa por id). Selección bulk se conserva (REGLA #18).
 */
import { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useTopBarCtx } from '../contexts/TopBarContext.js';
import { matchesSearch } from '../lib/searchTerms.js';
import { Icon } from '../components/Icon.js';
import { Recorrido } from '../components/ui/Recorrido.js';
import { useRecorrido } from '../hooks/useRecorrido.js';
import { FilterRow } from '../components/ui/FilterRow.js';
import { Modal } from '../components/ui/Modal.js';
import { Input } from '../components/ui/Input.js';
import { Textarea } from '../components/ui/Textarea.js';
import { Select } from '../components/ui/Select.js';
import { Button } from '../components/ui/Button.js';
import { Checkbox } from '../components/ui/Checkbox.js';
import { ListBandHeader } from '../components/ui/ListBandHeader.js';
import { SelectionActionBar } from '../components/ui/SelectionActionBar.js';
import { LoadingState } from '../components/ui/LoadingState.js';
import { ListState } from '../components/ui/ListState.js';
import { PillGroup, Pill } from '../components/PillGroup.js';
import { useToast } from '../components/ui/Toast.js';
import { TaskAccordion, needsAttention } from '../components/tasks/TaskAccordion.js';
import {
  useTasks, useCreateTask, useRunnerStatus,
  usePauseRunner, useResumeRunner,
  useBulkDeleteTasks, useBulkRetryTasks,
  type Task,
} from '../hooks/useTasks.js';

// ── Create modal (comportamiento intacto) ────────────────────────────────────

function CreateTaskModal({ onClose, onCreated, contact }: {
  onClose: () => void;
  onCreated: () => void;
  /** Cliente precargado — viene del + de la tarjeta Tareas de un contacto. */
  contact?: { id: number; name: string } | null;
}) {
  const { t } = useTranslation();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [type, setType] = useState('general');
  const [priority, setPriority] = useState('media');
  const [repeatMinutes, setRepeatMinutes] = useState('');
  const create = useCreateTask();

  function submit() {
    if (!title.trim()) return;
    create.mutate({
      ...(contact ? { contactId: contact.id } : {}),
      title: title.trim(), description: description.trim(), type, priority,
      ...(repeatMinutes.trim() ? { repeatMinutes: Math.max(1, parseInt(repeatMinutes)) } : {}),
    }, { onSuccess: () => onCreated() });
  }

  return (
    <Modal open onClose={onClose} size="md" title={t('tasks.modal.title')} preventClose={create.isPending}
      footer={
        <>
          <Button variant="outline" tone="muted" size="sm" disabled={create.isPending} onClick={onClose}>{t('common.cancel')}</Button>
          <Button variant="solid" tone="cyan" size="sm" loading={create.isPending} onClick={submit}>{t('tasks.modal.create')}</Button>
        </>
      }>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--evo-space-4)' }}>
        <Input label={`${t('tasks.fields.title')} *`} value={title} onChange={setTitle} autoFocus fullWidth />
        <Textarea label={t('tasks.fields.description')} value={description} onChange={setDescription} rows={3} fullWidth />
        <Select label={t('tasks.fields.type')} value={type} onChange={v => setType(v ?? 'general')}
          options={[
            { value: 'general', label: t('tasks.type.general') },
            { value: 'research', label: t('tasks.type.research') },
            { value: 'write', label: t('tasks.type.write') },
            { value: 'disk-check', label: t('tasks.type.disk-check') },
          ]} />
        <Select label={t('tasks.fields.priority')} value={priority} onChange={v => setPriority(v ?? 'media')}
          options={[
            { value: 'alta', label: t('tasks.priority.alta') },
            { value: 'media', label: t('tasks.priority.media') },
            { value: 'baja', label: t('tasks.priority.baja') },
          ]} />
        <Input label={t('tasks.fields.repeatMinutes')} type="number" value={repeatMinutes} onChange={setRepeatMinutes} fullWidth />
      </div>
    </Modal>
  );
}

// ── Bandas por estado ─────────────────────────────────────────────────────────

type BandKey = 'approval' | 'active' | 'closed';
const BAND_ORDER: BandKey[] = ['approval', 'active', 'closed'];

function bandOf(task: Task): BandKey {
  if (needsAttention(task)) return 'approval';
  if (task.status === 'done' || task.status === 'failed' || task.status === 'blocked') return 'closed';
  return 'active';
}

// La banda de grupo usa ListBandHeader (componente único del sistema): antes
// esta pantalla tenía su propia versión y no coincidía con las demás listas.

// ── Page ────────────────────────────────────────────────────────────────────

export function TasksPage() {
  const { t } = useTranslation();
  const toast = useToast();
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [precargado, setPrecargado] = useState<{ id: number; name: string } | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [collapsedBands, setCollapsedBands] = useState<Set<BandKey>>(new Set());
  // Varias abiertas a la vez — el default vive acá, no en un fallback del render.
  const [openIds, setOpenIds] = useState<Set<string>>(new Set());
  const openedFromUrl = useRef(false);

  // “+” de Relacionados: ?nuevo=1&contactId&contactName abre crear precargado.
  useEffect(() => {
    if (searchParams.get('nuevo') !== '1') return;
    const cid = parseInt(searchParams.get('contactId') ?? '');
    const cname = searchParams.get('contactName') ?? '';
    if (Number.isFinite(cid)) setPrecargado({ id: cid, name: cname || `#${cid}` });
    setShowCreate(true);
    const next = new URLSearchParams(searchParams);
    next.delete('nuevo'); next.delete('contactId'); next.delete('contactName');
    setSearchParams(next, { replace: true });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { data, isLoading, isError, error, refetch } = useTasks({});
  const { data: runner } = useRunnerStatus();
  const pause = usePauseRunner();
  const resume = useResumeRunner();
  const bulkDelete = useBulkDeleteTasks();
  const bulkRetry = useBulkRetryTasks();

  // Por palabra y no por frase — ver lib/searchTerms.ts. Van los dos campos:
  // antes solo se miraba plainSummary, y si estaba vacío el título quedaba
  // fuera de la búsqueda.
  const tasks = (data?.tasks ?? []).filter(tk =>
    matchesSearch(search, [tk.plainSummary, tk.title])
    && (!typeFilter || tk.type === typeFilter));

  // Deep-link ?open=<id>: expandir esa tarea y llevar el scroll a su posición.
  useEffect(() => {
    const openId = searchParams.get('open');
    if (!openId || openedFromUrl.current || tasks.length === 0) return;
    if (!tasks.some(tk => tk.id === openId)) return;
    openedFromUrl.current = true;
    setOpenIds(prev => new Set(prev).add(openId));
    setTimeout(() => {
      document.querySelector(`[data-task-id="${openId}"]`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 100);
  }, [searchParams, tasks]);

  function toggleSelect(id: string) {
    setSelected(prev => { const next = new Set(prev); next.has(id) ? next.delete(id) : next.add(id); return next; });
  }
  function clearSelection() { setSelected(new Set()); }
  function toggleOpen(id: string) {
    setOpenIds(prev => { const next = new Set(prev); next.has(id) ? next.delete(id) : next.add(id); return next; });
  }

  const { setOverride } = useTopBarCtx();
  useEffect(() => {
    setOverride({
      title: t('tasks.pageTitle'),
      heroIcon: 'tasks',
      heroIconColor: 'var(--evo-steel)',
      // El selector de modo se mudó a Configuración › Automatización › Evo
      // Brain (2026-09-02): decide si el motor ejecuta sin aprobación, que es
      // la misma pregunta que responden Autonomía por canal y Proactividad, y
      // ahí es donde se la busca. Acá quedaba a un clic de distancia de la
      // lista que gobierna, sin contexto de lo que implica.
      bottomActions: (
        <div data-tour-id="tasks-nuevo" style={{ display: 'flex' }}>
          <PillGroup size="md">
            <Pill tone="cyan" onClick={() => setShowCreate(true)}>{t('tasks.newButton')}</Pill>
          </PillGroup>
        </div>
      ),
    });
    return () => setOverride({});
  }, [setOverride, t]);

  const bands: Record<BandKey, Task[]> = { approval: [], active: [], closed: [] };
  for (const tk of tasks) bands[bandOf(tk)].push(tk);

  // ANTES del return: un hook debajo de un return condicional cambia la
  // cantidad de hooks entre renders y React tira la pantalla entera.
  const recorrido = useRecorrido('tasks', { hayDatos: tasks.length > 0, listo: !isLoading });

  return (
    <div className="flex flex-col h-full animate-fade-blur-in">
      <Recorrido
        pasos={recorrido.pasos}
        abierto={recorrido.abierto}
        onCerrar={recorrido.cerrar}
        onTerminar={recorrido.terminar}
      />
      <FilterRow>
        <FilterRow.Search value={search} onChange={setSearch} onSubmit={() => {}} onClear={() => setSearch('')} placeholder={t('tasks.searchPlaceholder')} />
        <FilterRow.Dropdown<string>
          value={typeFilter}
          placeholder={t('tasks.filters2.allTypes')}
          options={[
            { value: '', label: t('tasks.filters2.allTypes') },
            { value: 'general', label: t('tasks.type.general') },
            { value: 'research', label: t('tasks.type.research') },
            { value: 'write', label: t('tasks.type.write') },
            { value: 'disk-check', label: t('tasks.type.disk-check') },
            { value: 'sysadmin', label: t('tasks.type.sysadmin', { defaultValue: 'sysadmin' }) },
          ]}
          onChange={setTypeFilter}
        />
        <div style={{ flex: 1 }} />
        <Button variant="link" tone="cyan" size="sm" onClick={() => setOpenIds(new Set(tasks.map(tk => tk.id)))}>
          {t('tasks.expandAll')}
        </Button>
        <Button variant="link" tone="cyan" size="sm" onClick={() => setOpenIds(new Set())}>
          {t('tasks.collapseAll')}
        </Button>
      </FilterRow>

      {/* Franja del runner: contexto de TODO el módulo — si está pausado,
          ninguna “próxima ejecución” va a ocurrir. */}
      <div style={{
        padding: 'var(--evo-space-2) var(--evo-page-pad-x)',
        borderBottom: '1px solid var(--evo-stroke)',
        display: 'flex', alignItems: 'center', gap: 'var(--evo-space-4)', flexWrap: 'wrap',
        background: 'var(--evo-bg-2)',
      }}>
        {runner?.paused
          ? <Pill variant="outline" tone="amber" size="sm">{t('tasks.runner2.paused')}</Pill>
          : <Pill variant="outline" tone="cyan" size="sm">{t('tasks.runner2.running')}</Pill>}
        <span style={{ fontSize: 'var(--evo-fs-caption)', color: 'var(--evo-fg-3)', fontFamily: 'var(--evo-font)' }}>
          {t('tasks.runner2.ranToday')}: {runner?.runsToday ?? 0}{runner?.dailyCap ? ` / ${runner.dailyCap}` : ''}
        </span>
        {runner?.capReached && (
          <span style={{ fontSize: 'var(--evo-fs-caption)', color: 'var(--evo-danger)', fontFamily: 'var(--evo-font)' }}>
            {t('tasks.runner.capReached', { runs: runner.runsToday, cap: runner.dailyCap })}
          </span>
        )}
        <span style={{ flex: 1 }} />
        <span style={{ fontSize: 'var(--evo-fs-tiny)', color: 'var(--evo-fg-4)', fontFamily: 'var(--evo-font)' }}>
          {t('tasks.runner2.note')}
        </span>
        {runner?.paused
          ? <Button size="sm" variant="outline" tone="cyan" onClick={() => resume.mutate()}>{t('tasks.runner.run')}</Button>
          : <Button size="sm" variant="outline" tone="amber" onClick={() => pause.mutate()}>{t('tasks.runner.pause')}</Button>}
      </div>

      <SelectionActionBar
        visible={selected.size > 0}
        countLabel={t('tasks.selected', { count: selected.size })}
        onClearSelection={clearSelection}
      >
        <SelectionActionBar.Action
          label={t('tasks.actions.retry')}
          loading={bulkRetry.isPending}
          confirmRequired
          onClick={() => bulkRetry.mutate(Array.from(selected), {
            onSuccess: r => { clearSelection(); toast.info(t('tasks.bulk.retried', { retried: r.retried, skipped: r.skippedInProgress })); },
          })}
        />
        <SelectionActionBar.Divider />
        <SelectionActionBar.Action
          label={t('tasks.actions.delete')}
          tone="danger"
          loading={bulkDelete.isPending}
          confirmRequired
          onClick={() => bulkDelete.mutate(Array.from(selected), {
            onSuccess: r => { clearSelection(); toast.info(t('tasks.bulk.deleted', { deleted: r.deleted, skipped: r.skippedInProgress })); },
          })}
        />
      </SelectionActionBar>

      <div className="flex-1 overflow-auto" data-tour-id="tasks-lista" style={{ paddingBottom: 'var(--evo-space-6)' }}>
        {isLoading && <LoadingState size="sm" />}
        {!isLoading && (isError || tasks.length === 0) && (
          <ListState
            isLoading={false} isError={isError} error={error}
            isEmpty={tasks.length === 0} emptyTitle={t('tasks.empty')}
            onRetry={() => { void refetch(); }}
          >{null}</ListState>
        )}
        {BAND_ORDER.map(band => {
          const items = bands[band];
          if (items.length === 0) return null;
          const collapsed = collapsedBands.has(band);
          const ids = items.map(tk => tk.id);
          const allChecked = ids.length > 0 && ids.every(id => selected.has(id));
          const someChecked = ids.some(id => selected.has(id)) && !allChecked;
          return (
            // El ancla va en la PRIMERA banda: es donde el estado de una tarea
            // se ve de verdad, no en el filtro de arriba, que es por tipo.
            <div key={band} data-tour-id={band === BAND_ORDER[0] ? 'tasks-estado' : undefined}>
              <ListBandHeader
                plain
                label={t(`tasks.bands.${band}`)}
                count={items.length}
                collapsed={collapsed}
                onToggle={() => setCollapsedBands(prev => { const next = new Set(prev); next.has(band) ? next.delete(band) : next.add(band); return next; })}
                checked={allChecked}
                indeterminate={someChecked}
                onCheckChange={() => ids.forEach(id => {
                  const isSel = selected.has(id);
                  if (allChecked && isSel) toggleSelect(id);
                  else if (!allChecked && !isSel) toggleSelect(id);
                })}
              />
              {!collapsed && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--evo-log-gap)', padding: '0 var(--evo-log-inset) var(--evo-space-3)' }}>
                  {items.map(tk => (
                    <div
                      key={`w-${String(tk.id)}`}
                      data-tour-id={tk.id === tasks[0]?.id ? 'tasks-fila' : undefined}
                    >
                    <TaskAccordion
                      key={tk.id}
                      task={tk}
                      open={openIds.has(tk.id)}
                      onToggle={() => toggleOpen(tk.id)}
                      checked={selected.has(tk.id)}
                      onCheck={() => toggleSelect(tk.id)}
                    />
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {showCreate && <CreateTaskModal contact={precargado} onClose={() => { setShowCreate(false); setPrecargado(null); }} onCreated={() => { setShowCreate(false); setPrecargado(null); }} />}
    </div>
  );
}
